import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import type { FC } from "react";

interface InvitationMagicLinkConfirmProps {
  /** Masked invited address (e.g. `j••••@acme.com`), for display only. */
  maskedEmail: string | null;
  isSending: boolean;
  /** Whether the link has been sent, which switches to the confirmation. */
  isSent: boolean;
  onSend: () => void;
  onReset: () => void;
}

/**
 * Magic-link surface for the invitation flow. The invitee never types or sees
 * their full address — the link is sent server-side to the invited email, so
 * this only confirms the (masked) destination and triggers the send.
 */
export const InvitationMagicLinkConfirm: FC<
  InvitationMagicLinkConfirmProps
> = ({ maskedEmail, isSending, isSent, onSend, onReset }) => {
  const destination = maskedEmail ?? "your invitation email";

  if (isSent) {
    return (
      <div className="w-full text-center">
        <p className="text-sm text-foreground">
          We sent a link to{" "}
          <span className="font-medium text-foreground">{destination}</span>.
          Click the link in your email to finish signing in.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          Didn&apos;t receive it? Check your spam folder, or{" "}
          <button
            className="font-medium text-blue-600 hover:text-blue-500"
            onClick={onReset}
          >
            try again
          </button>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="w-full text-center">
      <p className="text-sm text-foreground">
        We&apos;ll email a one-time sign-in link to{" "}
        <span className="font-medium text-foreground">{destination}</span>.
      </p>
      <Button
        className="mx-auto mt-4"
        onClick={onSend}
        variant={ButtonVariant.Primary}
        isDisabled={isSending}
      >
        {isSending ? "Sending..." : "Send link"}
      </Button>
    </div>
  );
};
